import { z } from "zod";
/** Alphabetical by English name. */
export const STAGE_SESSION_LANGUAGES = [
    { code: "af", label: "Afrikaans", native: "Afrikaans", script: "latin" },
    { code: "sq", label: "Albanian", native: "Shqip", script: "latin" },
    { code: "ar", label: "Arabic", native: "العربية", script: "arabic", rtl: true },
    { code: "az", label: "Azerbaijani", native: "Azərbaycan dili", script: "latin" },
    { code: "eu", label: "Basque", native: "Euskara", script: "latin" },
    { code: "be", label: "Belarusian", native: "Беларуская", script: "cyrillic" },
    { code: "bn", label: "Bengali", native: "বাংলা", script: "bengali" },
    { code: "bs", label: "Bosnian", native: "Bosanski", script: "latin" },
    { code: "bg", label: "Bulgarian", native: "Български", script: "cyrillic" },
    { code: "ca", label: "Catalan", native: "Català", script: "latin" },
    { code: "zh", label: "Chinese", native: "中文", script: "han" },
    { code: "hr", label: "Croatian", native: "Hrvatski", script: "latin" },
    { code: "cs", label: "Czech", native: "Čeština", script: "latin" },
    { code: "da", label: "Danish", native: "Dansk", script: "latin" },
    { code: "nl", label: "Dutch", native: "Nederlands", script: "latin" },
    { code: "en", label: "English", native: "English", script: "latin" },
    { code: "et", label: "Estonian", native: "Eesti", script: "latin" },
    { code: "fi", label: "Finnish", native: "Suomi", script: "latin" },
    { code: "fr", label: "French", native: "Français", script: "latin" },
    { code: "gl", label: "Galician", native: "Galego", script: "latin" },
    { code: "de", label: "German", native: "Deutsch", script: "latin" },
    { code: "el", label: "Greek", native: "Ελληνικά", script: "greek" },
    { code: "gu", label: "Gujarati", native: "ગુજરાતી", script: "gujarati" },
    { code: "he", label: "Hebrew", native: "עברית", script: "hebrew", rtl: true },
    { code: "hi", label: "Hindi", native: "हिन्दी", script: "devanagari" },
    { code: "hu", label: "Hungarian", native: "Magyar", script: "latin" },
    { code: "id", label: "Indonesian", native: "Bahasa Indonesia", script: "latin" },
    { code: "it", label: "Italian", native: "Italiano", script: "latin" },
    { code: "ja", label: "Japanese", native: "日本語", script: "japanese" },
    { code: "kn", label: "Kannada", native: "ಕನ್ನಡ", script: "kannada" },
    { code: "kk", label: "Kazakh", native: "Қазақша", script: "cyrillic" },
    { code: "ko", label: "Korean", native: "한국어", script: "hangul" },
    { code: "lv", label: "Latvian", native: "Latviešu", script: "latin" },
    { code: "lt", label: "Lithuanian", native: "Lietuvių", script: "latin" },
    { code: "mk", label: "Macedonian", native: "Македонски", script: "cyrillic" },
    { code: "ms", label: "Malay", native: "Bahasa Melayu", script: "latin" },
    { code: "ml", label: "Malayalam", native: "മലയാളം", script: "malayalam" },
    { code: "mr", label: "Marathi", native: "मराठी", script: "devanagari" },
    { code: "no", label: "Norwegian", native: "Norsk", script: "latin" },
    { code: "fa", label: "Persian", native: "فارسی", script: "arabic", rtl: true },
    { code: "pl", label: "Polish", native: "Polski", script: "latin" },
    { code: "pt", label: "Portuguese", native: "Português", script: "latin" },
    { code: "pa", label: "Punjabi", native: "ਪੰਜਾਬੀ", script: "gurmukhi" },
    { code: "ro", label: "Romanian", native: "Română", script: "latin" },
    { code: "ru", label: "Russian", native: "Русский", script: "cyrillic" },
    { code: "sr", label: "Serbian", native: "Српски", script: "cyrillic" },
    { code: "sk", label: "Slovak", native: "Slovenčina", script: "latin" },
    { code: "sl", label: "Slovenian", native: "Slovenščina", script: "latin" },
    { code: "es", label: "Spanish", native: "Español", script: "latin" },
    { code: "sw", label: "Swahili", native: "Kiswahili", script: "latin" },
    { code: "sv", label: "Swedish", native: "Svenska", script: "latin" },
    { code: "tl", label: "Tagalog", native: "Tagalog", script: "latin" },
    { code: "ta", label: "Tamil", native: "தமிழ்", script: "tamil" },
    { code: "te", label: "Telugu", native: "తెలుగు", script: "telugu" },
    { code: "th", label: "Thai", native: "ไทย", script: "thai" },
    { code: "tr", label: "Turkish", native: "Türkçe", script: "latin" },
    { code: "uk", label: "Ukrainian", native: "Українська", script: "cyrillic" },
    { code: "ur", label: "Urdu", native: "اردو", script: "arabic", rtl: true },
    { code: "vi", label: "Vietnamese", native: "Tiếng Việt", script: "latin" },
    { code: "cy", label: "Welsh", native: "Cymraeg", script: "latin" },
];
export const STAGE_SESSION_LANGUAGE_CODES = STAGE_SESSION_LANGUAGES.map((l) => l.code);
/** A language code voicely will accept. */
export const stageSessionLanguageZod = z.enum(STAGE_SESSION_LANGUAGE_CODES);
export const languageByCode = (code) => STAGE_SESSION_LANGUAGES.find((l) => l.code === code);
/** "English" — the English name, or the code itself for one we do not know. */
export const languageLabel = (code) => languageByCode(code)?.label ?? code;
/** "हिन्दी" — what its speaker reads; falls back like `languageLabel`. */
export const languageNative = (code) => languageByCode(code)?.native ?? code;
/**
 * Two languages, or none: a show in one language has nothing to translate.
 * Shared by the event, stage and session inputs; lives here rather than on
 * one of them because `event.zod` imports `stage.zod`, and a helper on the
 * event side is still undefined when the stage schema evaluates.
 */
export const languagesDifferRefine = (v) => v.stageLanguage == null || v.hallLanguage == null || v.stageLanguage !== v.hallLanguage;
export const LANGUAGES_DIFFER_MESSAGE = {
    message: "Stage and hall languages must differ",
    path: ["hallLanguage"],
};
//# sourceMappingURL=languages.zod.js.map